"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { SetupStep } from "./components/SetupStep";
import { OrganizationService } from "@/services/OrganizationService";

interface FormData {
  name: string;
  phoneNumber: string;
  email: string;
}

interface Step {
  title: string;
  placeholder?: string;
  validate: (value: string) => boolean;
  errorMessage: string;
  helperText?: string;
  field: keyof FormData;
  mask?: (value: string) => string;
  type?: string;
}

const normalizePhoneDigits = (value: string): string => value.replace(/\D/g, "");

const steps: Step[] = [
  {
    title: "Nome da Organizacao",
    placeholder: "Nome da sua empresa",
    validate: (value: string) => value.trim().length > 0,
    errorMessage: "Informe o nome da organizacao para continuar.",
    helperText: "Esse e o unico campo obrigatorio.",
    field: "name",
  },
  {
    title: "Telefone (opcional)",
    placeholder: "(11) 99999-9999",
    field: "phoneNumber",
    mask: (value: string) => {
      const numbers = normalizePhoneDigits(value).slice(0, 11);
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 6) {
        return numbers.replace(/(\d{2})(\d{1,4})/, "($1) $2");
      }
      if (numbers.length <= 10) {
        return numbers.replace(/(\d{2})(\d{4})(\d{1,4})/, "($1) $2-$3");
      }
      return numbers.replace(/(\d{2})(\d{5})(\d{1,4})/, "($1) $2-$3");
    },
    validate: (value: string) => {
      const digits = normalizePhoneDigits(value);
      return digits.length === 0 || digits.length === 10 || digits.length === 11;
    },
    errorMessage: "Use um telefone valido com DDD, por exemplo: (11) 99999-9999.",
    helperText: "Pode deixar em branco e completar depois.",
  },
  {
    title: "E-mail (opcional)",
    placeholder: "email@empresa.com",
    type: "email",
    field: "email",
    validate: (value: string) => {
      const normalized = value.trim();
      return normalized === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
    },
    errorMessage: "Digite um e-mail valido ou deixe em branco.",
    helperText: "Usado para contato e notificacoes.",
  },
];

export default function OrganizationSetup() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phoneNumber: "",
    email: "",
  });
  const [shouldValidate, setShouldValidate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const validateCurrentStep = (): boolean => {
    const current = steps[currentStep];
    const currentValue = formData[current.field] || "";

    if (!current.validate(currentValue)) {
      setShouldValidate(true);
      return false;
    }

    setShouldValidate(false);
    return true;
  };

  const handleNext = () => {
    if (currentStep >= steps.length - 1) return;
    if (!validateCurrentStep()) return;
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setShouldValidate(false);
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCancel = () => {
    navigate("/organizacoes");
  };

  const handleConfirm = async () => {
    if (!validateCurrentStep()) return;

    try {
      setIsLoading(true);
      const service = new OrganizationService();
      const cleanedPhone = normalizePhoneDigits(formData.phoneNumber);
      const hasPhone = cleanedPhone.length === 10 || cleanedPhone.length === 11;

      await service.create({
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        ...(hasPhone
          ? {
              internationalCode: "55",
              ddd: cleanedPhone.slice(0, 2),
              phoneNumber: cleanedPhone.slice(2),
            }
          : {}),
      });

      toast.success("Organizacao criada com sucesso.");
      navigate("/organizacoes");
    } catch (error) {
      console.error("Failed to create organization:", error);
      toast.error("Nao foi possivel criar a organizacao. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="min-h-screen flex flex-col p-8 bg-[#D6D8CB]">
      <div className="flex items-center justify-between mb-8">
        <img
          src="https://fly.storage.tigris.dev/pedidoz-public-images/landing-page/logo-completa-sem-fundo2.PNG"
          alt="logo"
          className="h-[40px] object-contain"
        />
        <button
          onClick={handleCancel}
          className="p-2 rounded-full bg-[#050505] text-[#D6D8CB] transition-all duration-300 ease-in-out transform hover:scale-110"
        >
          <X size={24} />
        </button>
      </div>

      <div className="w-full bg-[#050505]/10 h-2 mb-6 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#EE7B11] transition-all duration-500 ease-in-out"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>
      <p className="text-sm text-[#050505]/70 mb-12 text-center">
        Passo {currentStep + 1} de {steps.length}
      </p>

      <div className="flex-grow flex flex-col justify-center items-center">
        <SetupStep
          label={currentStepData.title}
          value={formData[currentStepData.field] || ""}
          onChange={(value) => updateFormData(currentStepData.field, value)}
          placeholder={currentStepData.placeholder}
          type={currentStepData.type}
          mask={currentStepData.mask}
          validate={shouldValidate ? currentStepData.validate : undefined}
          helperText={currentStepData.helperText}
          errorMessage={currentStepData.errorMessage}
        />
      </div>

      <div className="flex justify-between mt-12">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 0 || isLoading}
          className="p-2 rounded-full bg-[#EE7B11] text-[#D6D8CB] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-in-out transform hover:scale-110"
        >
          <ArrowLeft size={24} />
        </button>

        {isLastStep ? (
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-2 rounded-full bg-[#EE7B11] text-[#D6D8CB] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-in-out transform hover:scale-110 min-w-[220px] justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span className="font-medium">Criando...</span>
              </>
            ) : (
              <>
                <span className="font-medium">Criar organizacao</span>
                <Check size={20} />
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={isLoading}
            className="p-2 rounded-full bg-[#EE7B11] text-[#D6D8CB] transition-all duration-300 ease-in-out transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowRight size={24} />
          </button>
        )}
      </div>
    </div>
  );
}
